import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { useRef, useState } from 'react';
import { DsIcon } from '../../../../components/icons/DsIcon';
import { DsTooltip } from '../../../../components/tooltip/DsTooltip';
import { useSettings } from '../../../@core/hooks/useSettings';
import type { Mode } from '../../../@core/types';

const ModeDropdown = () => {
  // States
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Refs
  const anchorRef = useRef<HTMLButtonElement>(null);

  // Hooks
  const { settings, updateSettings } = useSettings();

  const handleClose = () => {
    setOpen(false);
    setTooltipOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleModeSwitch = (mode: Mode) => {
    handleClose();

    if (settings.mode !== mode) {
      updateSettings({ mode: mode });
    }
  };

  const getModeIcon = () => {
    if (settings.mode === 'system') {
      return <DsIcon icon="SETTING" />;
    }
    if (settings.mode === 'dark') {
      return <DsIcon icon="DARK_MODE" />;
    }
    return <DsIcon icon="LIGHT_MODE" />;
  };

  return (
    <>
      <DsTooltip
        title={`${settings.mode} Mode`}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
        open={open ? false : !!tooltipOpen}
        PopperProps={{ className: 'capitalize' }}
      >
        <IconButton ref={anchorRef} onClick={handleToggle} className="!text-textPrimary">
          {getModeIcon()}
        </IconButton>
      </DsTooltip>
      <Popper
        open={open}
        transition
        disablePortal
        placement="bottom-start"
        anchorEl={anchorRef.current}
        className="min-is-[160px] !mbs-4 z-[1]"
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'right top' }}
          >
            <Paper className={settings.skin === 'bordered' ? 'border shadow-none' : 'shadow-lg'}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList onKeyDown={handleClose}>
                  <MenuItem
                    className="gap-3"
                    onClick={() => handleModeSwitch('light')}
                    selected={settings.mode === 'light'}
                  >
                    <DsIcon icon="LIGHT_MODE" />
                    Light
                  </MenuItem>
                  <MenuItem
                    className="gap-3"
                    onClick={() => handleModeSwitch('dark')}
                    selected={settings.mode === 'dark'}
                  >
                    <DsIcon icon="DARK_MODE" />
                    Dark
                  </MenuItem>
                  <MenuItem
                    className="gap-3"
                    onClick={() => handleModeSwitch('system')}
                    selected={settings.mode === 'system'}
                  >
                    <DsIcon icon="SETTING" />
                    System
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default ModeDropdown;
